import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ApiError } from "../../lib/api";
import {
  resendVerification,
  verifyEmailByCode,
  verifyEmailByToken,
} from "../../lib/auth";

interface LocationState {
  /** Pre-filled from the signup form so the user doesn't retype. */
  email?: string;
  /** Dev-mode short code surfaced from /v1/auth/register. */
  devCode?: string | null;
}

type Status = "idle" | "verifying" | "success" | "error";

const CODE_LEN = 6;

export function VerifyEmailPage(): JSX.Element {
  const location = useLocation();
  const state = (location.state ?? {}) as LocationState;
  const [params] = useSearchParams();

  const urlToken = params.get("token");
  const [email, setEmail] = useState(state.email ?? "");
  const [code, setCode] = useState(state.devCode ?? "");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<"idle" | "sent">("idle");

  // Magic-link path: when the user clicks the email button, we land
  // here with `?token=<64chars>`. Auto-submit and skip the form.
  useEffect(() => {
    if (urlToken !== null && urlToken.length === 64 && status === "idle") {
      void runWithToken(urlToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runWithToken(t: string): Promise<void> {
    setStatus("verifying");
    setError(null);
    try {
      await verifyEmailByToken(t);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(extractErrorMessage(err));
    }
  }

  async function runWithCode(): Promise<void> {
    setStatus("verifying");
    setError(null);
    try {
      await verifyEmailByCode({ code, email });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(extractErrorMessage(err));
    }
  }

  async function onResend(): Promise<void> {
    if (email === "") return;
    try {
      await resendVerification(email);
    } catch {
      // Endpoint never errors for security; ignore.
    }
    setResendStatus("sent");
  }

  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col justify-center gap-6 px-6 py-12">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-neutral-900">Verify your email</h1>
        {email !== "" ? (
          <p className="text-sm text-neutral-600">
            We sent a verification link to{" "}
            <strong className="font-medium text-neutral-900">{email}</strong>.
          </p>
        ) : (
          <p className="text-sm text-neutral-600">
            Enter the 6-character code from the email we sent you.
          </p>
        )}
      </header>

      {status === "success" ? (
        <div className="space-y-4 rounded-lg border border-success-200 bg-success-50 p-6 text-success-700">
          <p>Email verified. You can sign in now.</p>
          <Link to="/login">
            <Button variant="primary">Continue to sign-in</Button>
          </Link>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void runWithCode();
          }}
          className="space-y-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-sm"
        >
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label="Verification code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, CODE_LEN))}
            required
            minLength={CODE_LEN}
            maxLength={CODE_LEN}
            autoComplete="one-time-code"
            inputMode="text"
            placeholder="ABC123"
            hint="6 letters/digits — copy from the email."
            className="text-center font-mono text-lg tracking-[0.3em]"
          />
          {error !== null && (
            <p
              role="alert"
              className="rounded-md border border-error-200 bg-error-50 px-3 py-2 text-sm text-error-700"
            >
              {error}
            </p>
          )}
          <Button
            type="submit"
            variant="primary"
            fullWidth
            loading={status === "verifying"}
          >
            Verify email
          </Button>
        </form>
      )}

      {status !== "success" && (
        <div className="text-center">
          {resendStatus === "sent" ? (
            <p className="text-sm text-success-700">
              If that address belongs to an unverified account, a fresh email is on its way.
            </p>
          ) : (
            <Button variant="link" onClick={() => void onResend()} disabled={email === ""}>
              Resend verification email
            </Button>
          )}
        </div>
      )}
    </main>
  );
}

function extractErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return err.problem.detail || err.problem.title;
  }
  return "Verification failed. Please try again.";
}
